import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import toast, { Toaster } from 'react-hot-toast';
import { sanitizeInput } from '../../services/securityService';
import { checkRateLimit } from '../../services/securityService';
import { logAuditEvent } from '../../services/auditLogService';

const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [tempUserId, setTempUserId] = useState(null);
  const [tempAccessToken, setTempAccessToken] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Check rate limit (pass email as pre-auth identifier — no userId yet)
    const rateLimitResult = await checkRateLimit('auth', email?.toLowerCase()?.trim());
    if (!rateLimitResult?.allowed) {
      toast?.error('Too many login attempts. Please try again later.');
      return;
    }

    if (!email || !password) {
      toast?.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);

    try {
      // // Execute reCAPTCHA v3 -- TEMPORARILY BYPASSED
      // const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
      // if (!recaptchaSiteKey) throw new Error('Missing VITE_RECAPTCHA_SITE_KEY environment variable');
      // const recaptchaToken = await window.grecaptcha?.execute(
      //   recaptchaSiteKey,
      //   { action: 'login' }
      // );

      // if (!recaptchaToken) {
      //   toast?.error('reCAPTCHA verification failed. Please try again.');
      //   setIsLoading(false);
      //   return;
      // }

      // // Verify reCAPTCHA token server-side
      // const { data: verifyData, error: verifyError } = await supabase?.functions?.invoke('verify-recaptcha', {
      //   body: { token: recaptchaToken, action: 'login' }
      // });

      // if (verifyError || !verifyData?.success || verifyData?.score < 0.5) {
      //   toast?.error('Bot detection failed. Please try again.');
      //   await logAuditEvent({
      //     actionType: 'recaptcha_failed',
      //     actionDescription: `reCAPTCHA verification failed for login attempt: ${email}`,
      //     severity: 'warning'
      //   });
      //   setIsLoading(false);
      //   return;
      // }

      // Sanitize inputs
      const sanitizedEmail = sanitizeInput(email);
      const sanitizedPassword = sanitizeInput(password);

      const { data, error } = await signIn(sanitizedEmail, sanitizedPassword);

      if (error) {
        const msg = error?.message || '';
        if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid email or password')) {
          toast?.error('Incorrect email or password. Please try again.');
        } else if (msg.toLowerCase().includes('email not confirmed')) {
          toast?.error('Please verify your email address before signing in.');
        } else if (msg.toLowerCase().includes('too many requests')) {
          toast?.error('Too many login attempts. Please wait a moment and try again.');
        } else {
          toast?.error(msg || 'Login failed. Please try again.');
        }
        // Log failed login attempt
        await logAuditEvent({
          actionType: 'login_failed',
          actionDescription: `Failed login attempt for ${sanitizedEmail}`,
          severity: 'warning'
        });
        setIsLoading(false);
        return;
      }

      // Log successful login
      await logAuditEvent({
        actionType: 'login',
        actionDescription: 'User logged in successfully',
        severity: 'info'
      });

      // Check if 2FA is enabled
      if (data?.user) {
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('twofa_enabled')
          ?.eq('id', data?.user?.id)
          ?.single();

        if (profile?.twofa_enabled) {
          setTempUserId(data?.user?.id);
          setTempAccessToken(data?.session?.access_token ?? null);
          setShow2FAModal(true);
          setIsLoading(false);
          return;
        }
      }

      toast?.success('Login successful!');
      
      // Fetch user profile to determine role
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', data?.user?.id)
        ?.single();

      // Redirect based on role
      const userRole = profile?.role || 'client';
      if (userRole === 'admin') {
        navigate('/admin-dashboard');
      } else if (userRole === 'expert') {
        navigate('/expert-dashboard');
      } else {
        navigate('/client-dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast?.error('An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e?.preventDefault();
    
    if (!twoFACode || twoFACode.trim().length < 6) {
      toast.error('Please enter a verification code', { id: 'twofa' });
      return;
    }

    setIsLoading(true);
    setTwoFAError('');

    try {
      const { verify2FACode } = await import('../../services/authService');
      const result = await verify2FACode(tempUserId, twoFACode, tempAccessToken);

      if (!result?.success) {
        const msg = result?.error || 'Invalid verification code. Please check your authenticator app and try again.';
        setTwoFAError(msg);
        setTwoFACode('');
        return;
      }

      toast.success('Login successful!');
      
      // Fetch user profile to determine role
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', tempUserId)
        ?.single();

      // Redirect based on role
      const userRole = profile?.role || 'client';
      if (userRole === 'admin') {
        navigate('/admin-dashboard');
      } else if (userRole === 'expert') {
        navigate('/expert-dashboard');
      } else {
        navigate('/client-dashboard');
      }
    } catch (error) {
      setTwoFAError('Verification failed. Please try again.');
      setTwoFACode('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 px-4">
      <Toaster position="top-right" />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-16 object-contain mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-foreground mb-2">Welcome Back</h1>
          <p className="text-foreground-secondary">Sign in to your FireCaseIQ account</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="you@example.com"
                disabled={isLoading}
                className="w-full"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  placeholder="••••••••"
                  disabled={isLoading}
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-secondary hover:text-foreground"
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-border text-primary focus:ring-primary" />
                <span className="ml-2 text-sm text-foreground-secondary">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-primary hover:text-primary/80">
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-foreground-secondary">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:text-primary/80 font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-foreground-secondary hover:text-foreground flex items-center justify-center">
            <Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
      {/* 2FA Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                <Icon name="Shield" className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Two-Factor Authentication</h2>
              <p className="text-foreground-secondary">Enter the 6-digit code from your authenticator app</p>
            </div>

            <form onSubmit={handle2FASubmit} className="space-y-6">
              <div>
                <Input
                  type="text"
                  value={twoFACode}
                  onChange={(e) => {
                    const raw = e?.target?.value?.replace(/[^0-9A-Fa-f]/g, '');
                    const hasLetter = /[A-Fa-f]/.test(raw);
                    if (hasLetter) {
                      // Backup code mode: auto-insert hyphen after 5 chars
                      const formatted = raw.length > 5
                        ? raw.slice(0, 5) + '-' + raw.slice(5, 10)
                        : raw;
                      setTwoFACode(formatted);
                    } else {
                      // TOTP mode: digits only, max 6
                      setTwoFACode(raw.slice(0, 6));
                    }
                  }}
                  placeholder="000000 or XXXXX-XXXXX"
                  maxLength={11}
                  className="w-full text-center text-2xl tracking-widest"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                {twoFAError && (
                  <div className="rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive text-center">
                    {twoFAError}
                  </div>
                )}

                <Button
                  type="button"
                  onClick={() => {
                    setShow2FAModal(false);
                    setTwoFACode('');
                    setTwoFAError('');
                    setTempAccessToken(null);
                    setIsLoading(false);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !twoFACode.trim()}
                  className="flex-1"
                >
                  {isLoading ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
            </form>

            <p className="text-xs text-foreground-secondary text-center mt-4">
              Lost your device? Use one of your backup codes instead.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;